import {useEffect} from 'react';
import {fetchIdentificationInfo} from '@rocket-ui/lk-api';
import {
	extractInnDigits,
	mapIdentificationDocumentToPassportDetails,
	RegistrationStep,
	type PassportDetailsStepValues,
} from 'features/registration';
import type {RegistrationWizardState} from './useRegistrationWizardState';

/** Заполняет только пустые поля — введённое пользователем не перетирается. */
const fillEmpty = (
	current: PassportDetailsStepValues,
	prefill: Partial<PassportDetailsStepValues>,
): PassportDetailsStepValues => {
	const next = {...current};

	for (const key of Object.keys(prefill) as (keyof PassportDetailsStepValues)[]) {
		if (!next[key] && prefill[key]) {
			next[key] = prefill[key] as string;
		}
	}

	return next;
};

const readRegistrationAddress = (document: unknown, placeOfResidence?: string | null): string => {
	if (document && typeof document === 'object' && 'registrationAddress' in document) {
		const value = (document as {registrationAddress?: string | null}).registrationAddress?.trim();

		if (value) {
			return value;
		}
	}

	return placeOfResidence?.trim() ?? '';
};

/** Шаг «Введите паспортные данные»: подставляем ранее сохранённые паспорт и ИНН из GET /identification/info. */
export const usePassportDetailsPrefill = (state: RegistrationWizardState, usesForeignDocumentDetails: boolean) => {
	const {documentsFlow, setDocumentsUpload, setForeignDocumentDetails, setInnStep, setPassportDetails, step} =
		state;

	useEffect(() => {
		if (step !== RegistrationStep.DocumentDetails) {
			return;
		}

		let cancelled = false;

		void fetchIdentificationInfo()
			.then(({data}) => {
				if (cancelled) {
					return;
				}

				const placeOfResidence = data?.info?.placeOfResidence?.trim() ?? '';

				if (documentsFlow.residence && placeOfResidence) {
					setDocumentsUpload((current) =>
						current.residenceAddress ? current : {...current, residenceAddress: placeOfResidence},
					);
				}

				if (usesForeignDocumentDetails) {
					const registrationAddress = readRegistrationAddress(
						data?.identificationDocument,
						documentsFlow.residence ? null : data?.info?.placeOfResidence,
					);

					if (registrationAddress) {
						setForeignDocumentDetails((current) =>
							current.registrationAddress ? current : {...current, registrationAddress},
						);
					}

					return;
				}

				const prefill = mapIdentificationDocumentToPassportDetails(data?.identificationDocument);

				if (Object.keys(prefill).length > 0) {
					setPassportDetails((current) => fillEmpty(current, prefill));
				}

				const inn = extractInnDigits(data?.info?.inn ?? '');

				if (inn) {
					setInnStep((current) => (current.inn ? current : {inn}));
				}
			})
			.catch(() => {});

		return () => {
			cancelled = true;
		};
	}, [
		documentsFlow.residence,
		setDocumentsUpload,
		setForeignDocumentDetails,
		setInnStep,
		setPassportDetails,
		step,
		usesForeignDocumentDetails,
	]);
};
